import { Helmet } from 'react-helmet-async';
import { type PageSEO, SITE_URL } from '../seo';

interface SEOHeadProps {
  page: PageSEO;
}

const ORG_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PartnerBuddy',
  url: SITE_URL,
  logo: `${SITE_URL}/partnerbuddy-logo.png`,
};

const APP_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'PartnerBuddy',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description:
    'AI-powered assistant for managing AWS Partner Central ACE opportunities through Slack and Microsoft Teams.',
  offers: {
    '@type': 'Offer',
    price: '100.00',
    priceCurrency: 'USD',
    priceValidUntil: '2026-12-31',
  },
};

export default function SEOHead({ page }: SEOHeadProps) {
  const canonicalUrl = `${SITE_URL}${page.path}`;
  const ogImage = `${SITE_URL}/PartnerBuddy_social.png`;
  const isHome = page.path === '/';

  return (
    <Helmet>
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      <link rel="canonical" href={canonicalUrl} />

      {page.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="PartnerBuddy - AI-powered assistant for AWS Partner Central ACE opportunities" />
      <meta property="og:site_name" content="PartnerBuddy" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="PartnerBuddy - AI-powered assistant for AWS Partner Central ACE opportunities" />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ORG_JSON_LD)}</script>
      {isHome && (
        <script type="application/ld+json">
          {JSON.stringify(APP_JSON_LD)}
        </script>
      )}
    </Helmet>
  );
}
